/* eslint-disable @typescript-eslint/no-unsafe-call */
import { isSameOrigin, prefixCheck } from "@helper/utils";
import { ChangeEvent, KeyboardEvent, SubmitEvent, useState } from "react";

type SearchInputProps = {
  /**
   * Checks whether the search input is displayed.
   */
  expanded: boolean;

  /**
   * Additional CSS classes for the search container.
   */
  className?: string;

  /**
   * Initial value displayed in the search input.
   */
  initSearchValue?: string;
};

/**
 * Renders the expandable search input.
 *
 * Handles search input changes and submits the search query
 * when the form is submitted or the Enter key is pressed.
 *
 * @param props - Search input configuration.
 * @returns The search input when expanded, otherwise null.
 */
export function SearchInput({
  expanded,
  className,
  initSearchValue = "",
}: SearchInputProps) {
  const [searchValue, setSearchValue] = useState(initSearchValue);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // const sanitized = sanitizeSearchKeyword(event.target.value);
    setSearchValue(event.target.value);
  };

  const handleSearch = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (!searchValue.trim()) return;
      if (prefixCheck(searchValue)) return;
      if (!isSameOrigin(window.origin)) return;
      window.location.href = `/search/site/${encodeURIComponent(searchValue)}`;
    }
  };

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!searchValue.trim()) return;
    if (prefixCheck(searchValue)) return;
    if (!isSameOrigin(window.origin)) return;
    window.location.href = `/search/site/${encodeURIComponent(searchValue)}`;
  };

  return (
    <>
      {expanded ? (
        <div data-testid="search" className={className}>
          <div
            data-testid="searchExpanded"
            className="text-center border-t border-solid border-gray-400 lg:border-none pt-sm lg:pt-0 relative"
          >
            <form onSubmit={handleSubmit} className="flex">
              <input
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={handleInputChange}
                onKeyDown={handleSearch}
                className="flex-auto border border-solid border-gray-900 rounded rounded-tr-none rounded-br-none py-1.5 px-3 outline-none focus:border-pink-400 focus:shadow-5xl focus:z-10 text-xs text-gray-1000 font-primary placeholder-gray-1000 placeholder-opacity-100"
                maxLength={100}
              />
              <button
                type="submit"
                aria-label="Submit Search"
                className="text-center rounded bg-100 rounded-tl-none rounded-bl-none text-white-100 py-1.5 px-3 leading-loose"
              >
                தேடல்
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
